import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import "@/assets/css/style.css";
import "./ContactUs.module.css";
import { useAppSelector } from "@/store/hooks";
import toast from "react-hot-toast";

const ContactDetails: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);

  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");

  const [address, setAddress] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [whatsappNo, setWhatsappNo] = useState("");
  const [email, setEmail] = useState("");
  const [googleMap, setGoogleMap] = useState("");

  const [loading, setLoading] = useState(false);

  // ✅ Fetch existing details
  useEffect(() => {
    fetch(`${baseUrl}/contact-us`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          const c = data.data;
          setAddress(c.address || "");
          setMobileNo(c.mobileNo || "");
          setWhatsappNo(c.whatsappNo || "");
          setEmail(c.email || "");
          setGoogleMap(c.googleMap || "");
          if (c.image) setPreviewUrl(c.image);
        }
      })
      .catch((err) => console.error("Fetch error:", err));
  }, [token, baseUrl]);

  // ✅ Preview uploaded image
  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [image]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setImage(e.target.files[0]);
  };

  // ✅ Submit handler
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      if (image) 
        formData.append("image", image);
      else 
        formData.append("image", ""); // To handle no image case
      formData.append("address", address);
      formData.append("mobileNo", mobileNo);
      formData.append("whatsappNo", whatsappNo);
      formData.append("email", email);
      formData.append("googleMap", googleMap);

      const res = await fetch(`${baseUrl}/contact-us/update`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Contact Details updated!");
        // ✅ Refetch to confirm changes
        setTimeout(() => {
          navigate("/contact-us"); // Go to UI page to see updates
        }, 500);
      } else {
        toast.error(data.message || "Something went wrong!");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="side-app">
      <div className="main-container container-fluid">
        <div className="page-header">
          <h1 className="page-title">Update Contact Us Details</h1>
        </div>

        <form className="card" onSubmit={handleSubmit}>
          <div className="card-body">
            {/* Image Upload */}
            <div className="row mb-5">
              <div className="col-lg-4">
                <div
                  className="upload-box"
                  onClick={() => document.getElementById("fileInput")?.click()}
                  style={{
                    border: "2px dashed #bbb",
                    borderRadius: "12px",
                    padding: "30px",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  <p>Drag & Drop or Click to Upload</p>
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </div>
              </div>
              <div className="col-lg-4">
                {previewUrl && (
                  <div>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ height: 180, width: 300, objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger mt-2"
                      onClick={() => {
                        setImage(null);
                        setPreviewUrl("");
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                className="form-control"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            {/* Mobile */}
            <div className="form-group">
              <label>Mobile No.</label>
              <input
                type="text"
                className="form-control"
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
              />
            </div>

            {/* Whatsapp */}
            <div className="form-group">
              <label>Whatsapp No.</label>
              <input
                type="text"
                className="form-control"
                value={whatsappNo}
                onChange={(e) => setWhatsappNo(e.target.value)}
              />
            </div>

            {/* Email */}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Google Map */}
            <div className="form-group">
              <label>Google Map (Embed Code)</label>
              <textarea
                className="form-control"
                value={googleMap}
                onChange={(e) => setGoogleMap(e.target.value)}
                rows={5}
                placeholder="Paste Google Maps embed code here"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactDetails;


